import React, { useState } from 'react';
import { renderMarkdownToHtml } from 'Utils/formatting';
import CoreMarkdownEditor from 'Components/SbMarkdownEditor/CoreMarkdownEditor';
import { VIEWMODE } from 'constants/markdown.constants';
import { BLOCK_TYPES,DECISION } from 'constants/jobs.constants';
import SbButton from 'uiCore/SbButton/SbButton';
import {
    LineStyled,
    BlockHeaderStyled,
    BlockBodyStyled,
    ContentTextStyled,
    StatusBadgeStyled,
    ActionPopupStyled,
    PopupCloseStyled,
    MiniEditorWrapperStyled,
    EditorControlsStyled,
} from './HumanEditorV2.styles';

const HumanEditorBlockV2 = ({
    block,
    onUpdate,
    onMove,
    onRevert,
    onDelete,
    isFirst,
    isLast,
    showActionMenu,
    onToggleMenu,
    closeMenu,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [editValue, setEditValue] = useState(block.content);

    const isInteractive = block.type !== BLOCK_TYPES.LOCKED;
    const isActive = isEditing || isMoving;

    const handleSave = () => {
        onUpdate(block.id, editValue);
        setIsEditing(false);
        closeMenu();
    };

    const renderBadge = () => {
        if (block.type === BLOCK_TYPES.NEEDS_WORK) {
            return (
                <StatusBadgeStyled $variant="review">
                    ⚠️ REVIEW: {block.comment}
                </StatusBadgeStyled>
            );
        }
        if (block.type === BLOCK_TYPES.GHOST) {
            return (
                <StatusBadgeStyled $variant="ghost">
                    👻 RESTORE: {block.comment}
                </StatusBadgeStyled>
            );
        }
        if (block.type === BLOCK_TYPES.MODIFIED) {
            const isEdited = block.content.trim() !== block.originalContent.trim();
            return (
                <StatusBadgeStyled $variant="modified">
                    {isEdited ? '✅ EDITED' : '✅ MOVED'}
                </StatusBadgeStyled>
            );
        }
        return null;
    };

    const badge = renderBadge();

    return (
        <LineStyled $type={block.type.toLowerCase()} $active={isActive}>
            {badge && <BlockHeaderStyled>{badge}</BlockHeaderStyled>}

            <BlockBodyStyled>
                {/* VIEW MODE */}
                {!isEditing && (
                    <ContentTextStyled
                        $clickable={isInteractive}
                        onClick={() => !isMoving && isInteractive && onToggleMenu()}
                        dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(block.content) }}
                    />
                )}

                {/* ACTION POPUP */}
                {showActionMenu && !isEditing && !isMoving && isInteractive && (
                    <ActionPopupStyled>
                        {block.type === BLOCK_TYPES.GHOST ? (
                            <SbButton
                                color="green" variant="solid" size="small"
                                onClick={() => {
                                    onUpdate(block.id, block.content);
                                    setIsEditing(true);
                                    closeMenu();
                                }}
                            >
                                <span>✚</span> Restore
                            </SbButton>
                        ) : (
                            <>
                                <SbButton
                                    color="blue" variant="solid" size="small"
                                    onClick={() => {
                                        setEditValue(block.content);
                                        setIsEditing(true);
                                        closeMenu();
                                    }}
                                >
                                    <span>✎</span> Edit
                                </SbButton>

                                <SbButton
                                    variant="solid" size="small"
                                    onClick={() => {
                                        setIsMoving(true);
                                        closeMenu();
                                    }}
                                >
                                    <span>↔</span> Move
                                </SbButton>

                                {block.type === DECISION.MODIFIED && (
                                    <SbButton
                                        color="gold" variant="solid" size="small"
                                        onClick={() => {
                                            onRevert(block.id);
                                            closeMenu();
                                        }}
                                    >
                                        <span>↩</span> Revert
                                    </SbButton>
                                )}
                            </>
                        )}
                        <SbButton color="red" variant="solid" size="small" onClick={() => onDelete(block.id)}>
                            <span>🗑</span> Delete
                        </SbButton>
                        <PopupCloseStyled onClick={closeMenu}>×</PopupCloseStyled>
                    </ActionPopupStyled>
                )}

                {/* EDIT MODE */}
                {isEditing && (
                    <MiniEditorWrapperStyled>
                        <CoreMarkdownEditor
                            initialMarkdown={block.content}
                            viewMode={VIEWMODE.EDIT}
                            onChange={setEditValue}
                        />
                        <EditorControlsStyled>
                            <SbButton color="green" variant="solid" size="small" onClick={handleSave}>
                                Save
                            </SbButton>
                            <SbButton
                                variant="outlined" size="small"
                                onClick={() => { setIsEditing(false); setEditValue(block.content); }}
                            >
                                Cancel
                            </SbButton>
                        </EditorControlsStyled>
                    </MiniEditorWrapperStyled>
                )}

                {/* MOVE MODE */}
                {isMoving && (
                    <ActionPopupStyled $top={-40}>
                        <SbButton variant="solid" size="small" disabled={isFirst} onClick={() => onMove(block.id, -1)}>
                            ⬆
                        </SbButton>
                        <SbButton variant="solid" size="small" disabled={isLast} onClick={() => onMove(block.id, 1)}>
                            ⬇
                        </SbButton>
                        <SbButton color="green" variant="solid" size="small" onClick={() => setIsMoving(false)}>
                            ✔ Confirm
                        </SbButton>
                    </ActionPopupStyled>
                )}
            </BlockBodyStyled>
        </LineStyled>
    );
};

export default HumanEditorBlockV2;
